/**
 * @file element-warnings.ts
 * @description Предупреждения об элементах секции — не запреты (phase19.16,
 * партия SB120, этап 3а «элементы»; site-builder.md SB120 «платформа держит
 * закон и доступность как предупреждение», SB7, SB9, SB99; проект данных
 * db-design-sb120-stage3 §6, §10 п. 1; проект UI uiux-sb120-stage3 §2–§3,
 * §7). Образец — look-warnings.ts: чистая функция по данным, без DOM;
 * кабинет показывает «!» / «✕» у группы и в списке замечаний секции,
 * публикацию предупреждения не блокируют.
 *
 * Что проверяется:
 *   - контраст своего цвета части к фону под элементом (свой фон колонки →
 *     подложка секции → фон секции; у кнопки с заливкой — к заливке), у
 *     надписи на фото — худший случай: плашка на чёрном и на белом фото;
 *     фон-фото и плитки «поверх фото» не считаются (число зависит от снимка);
 *   - кнопка ниже 48 px; мелкий главный заголовок (H1 < 24 px на ПК или
 *     < 21 px на телефоне: заголовок H1, шапка раздела H1, название товара);
 *   - надпись на фото без плашки (плотность 0);
 *   - больше 3 семейств шрифтов на странице (тема + остальное + элементы);
 *   - свой шрифт элемента, которого нет у магазина (удалён) — на сайте
 *     сейчас шрифт роли (`element_font_missing`).
 *
 * Этап 3б (uiux-sb120-stage3 §3, §7): 7–8 плиток или категорий в ряд
 * (`grid_cols_many`), светлый текст плиток без затемнения при фото
 * (`tile_light_no_shade`), смена объявлений быстрее 5 с (`announcement_fast`),
 * контраст текста таблицы к фону шапки и полос — прежними кодами контраста
 * с путём к `head` / `stripe`. Части новых видов: у категорий «поверх фото»
 * не считаются (фото), у отзывов и объявлений карточками — к фону карточки
 * сайта (`cardBg`), у остальных — к фону колонки.
 */

import { compositeColors, contrastRatio, type RgbaColor } from "../cabinet-theme/contrast.js";
import { colorRefHex } from "../site-theme/color-ref.js";
import { BLACK, requireHexColor, toHexColor, WHITE } from "../site-theme/color.js";
import { resolveSurface, SITE_CONTRAST_LARGE_MIN, SITE_CONTRAST_TEXT_MIN } from "../site-theme/contrast.js";
import type { FontRoleTheme } from "../site-theme/font-roles.js";
import type { SiteFontRegistry } from "../site-theme/fonts.js";
import type { SiteColors } from "../site-theme/keys.js";
import { resolveSurfaceFill, surfaceToneOf } from "../site-theme/surface-fill.js";
import type { SiteColorRef } from "../site-theme/color-ref.js";
import type { SiteTextColor } from "../site-theme/text-color.js";
import { countFontFamilies, phoneFontSize, sectionTypoFonts, themeFontKeys, type TypoPart } from "./element-typo.js";
import { SECTION_LOOK_LIMITS, SECTION_WARNING_LIMITS } from "./look-limits.js";
import { tileShadeOf } from "./element-grid.js";
import type { SectionElement, SectionElementOf, SectionOverlay, SectionPayload } from "./schema.js";

export type SectionElementWarningCode =
  | "text_contrast_low"
  | "text_contrast_very_low"
  | "button_height_lt48"
  | "fonts_over_3"
  | "h1_small"
  | "overlay_no_plate"
  | "element_font_missing"
  | "grid_cols_many"
  | "tile_light_no_shade"
  | "announcement_fast";

/** Предупреждение: код, путь поля в payload и число для подписи (контраст, px, число шрифтов). */
export interface SectionElementWarning {
  code: SectionElementWarningCode;
  path: string;
  /** Контраст (до десятых, с отбрасыванием), px размера или высоты, число семейств. */
  value?: number;
}

/** Тексты словами владельца (uiux-sb120-stage3 §2.1, §3, §4, §7). */
export const SECTION_ELEMENT_WARNING_MESSAGES: Readonly<Record<SectionElementWarningCode, string>> = {
  text_contrast_low: "Контраст ниже 4,5:1 — читается только крупный текст.",
  text_contrast_very_low: "Контраст ниже 3:1 — текст почти не виден.",
  button_height_lt48: "Ниже 48 px — пальцем трудно попасть.",
  fonts_over_3: "На странице больше 3 шрифтов — загрузка медленнее. Лучше до 3.",
  h1_small: "Главный заголовок мельче обычного — теряется на странице.",
  overlay_no_plate: "Без плашки надпись читается не на каждом фото — проверьте на канве.",
  element_font_missing: "Шрифт удалён из медиатеки — на сайте сейчас шрифт роли. Выберите другой.",
  grid_cols_many: "7–8 в ряд — на 1280 плитка ≈ 140 px, подпись не влезет.",
  tile_light_no_shade: "Светлый текст без затемнения — проверьте на канве.",
  announcement_fast: "Объявление не успеют дочитать.",
};

/** Что нужно от темы: цвета (контраст) и шрифты ролей (число семейств). */
export interface SectionElementWarningTheme {
  readonly colors: SiteColors;
  readonly fonts: FontRoleTheme["fonts"];
}

export interface SectionElementWarningOptions {
  /** Тема черновика; нет — контраст и число шрифтов не проверяются. */
  theme?: SectionElementWarningTheme;
  /** Ключи шрифтов остальной страницы (шапка, подвал, другие секции) — для «больше 3». */
  pageFontKeys?: readonly string[];
}

const W = SECTION_WARNING_LIMITS;

/** Контраст до десятых с отбрасыванием (как `formatContrast`: 4,49 → 4,4). */
const floorTenth = (ratio: number): number => Math.floor(ratio * 10) / 10;

/** Код по контрасту; null — читается. */
function contrastCode(ratio: number): SectionElementWarningCode | null {
  if (ratio < SITE_CONTRAST_LARGE_MIN) return "text_contrast_very_low";
  if (ratio < SITE_CONTRAST_TEXT_MIN) return "text_contrast_low";
  return null;
}

/** Цвет текста → непрозрачный поверх фона. */
function textOn(color: SiteTextColor, colors: SiteColors, bg: RgbaColor): RgbaColor {
  return compositeColors(requireHexColor(color.kind === "role" ? colors[color.role] : color.color), bg);
}

/** Фон под элементом колонки `col`; null — фото (контраст по снимку не считается). */
function backgroundOf(payload: SectionPayload, col: number, colors: SiteColors): RgbaColor | null {
  const columnBg = payload.columns.items[col]?.bg;
  if (columnBg) return requireHexColor(resolveSurfaceFill(columnBg, { colors }).background);
  if (payload.look.card.on) return resolveSurface(colors, "cardBg");
  const bg = payload.look.bg;
  switch (bg.kind) {
    case "media":
      return null;
    case "custom":
      return requireHexColor(resolveSurfaceFill(bg, { colors }).background);
    case "role":
      return resolveSurface(colors, bg.role);
    default:
      return resolveSurface(colors, "pageBg");
  }
}

/** Части элемента, цвет которых лежит на фото (плитки и категории «поверх фото»), — не проверяются. */
function onOwnPhoto(el: SectionElement): boolean {
  return (el.kind === "tiles" || el.kind === "categories") && el.look === "overlay";
}

/** Текст лежит на карточке сайта (`site-card`): отзывы и объявления карточками (этап 3б). */
function onSiteCard(el: SectionElement): boolean {
  return el.kind === "reviews" || (el.kind === "announcements" && el.layout === "cards");
}

type Push = (warning: SectionElementWarning) => void;

/** Контраст частей `typo` со своим цветом к фону `bg`. */
function typoContrast(typo: Readonly<Record<string, TypoPart | undefined>> | undefined, path: string, bg: RgbaColor, colors: SiteColors, push: Push): void {
  if (!typo) return;
  for (const [part, value] of Object.entries(typo)) {
    if (!value?.color) continue;
    const ratio = contrastRatio(textOn(value.color, colors, bg), bg);
    const code = contrastCode(ratio);
    if (code) push({ code, path: `${path}.typo.${part}.color`, value: floorTenth(ratio) });
  }
}

/** Надпись на фото: худший контраст на плашке поверх чёрного и белого фото. */
function overlayContrast(overlay: SectionOverlay, path: string, colors: SiteColors, push: Push): void {
  const fill = requireHexColor(overlay.plate?.fill ? colorRefHex(overlay.plate.fill, colors) : "#000000");
  const alpha = fill.a * ((overlay.plate?.density ?? SECTION_LOOK_LIMITS.overlayPlate.default) / 100);
  const color: SiteTextColor = overlay.typo?.main?.color ?? { kind: "role", role: "onDark" };
  let worst = Infinity;
  for (const photo of [BLACK, WHITE]) {
    const bg = compositeColors({ ...fill, a: alpha }, photo);
    worst = Math.min(worst, contrastRatio(textOn(color, colors, bg), bg));
  }
  const code = contrastCode(worst);
  const where = overlay.typo?.main?.color ? `${path}.typo.main.color` : `${path}.plate`;
  if (code) push({ code, path: where, value: floorTenth(worst) });
}

/** Мелкий H1: размер ПК < 24 или телефона < 21 (своё число или правило телефона). */
function h1Small(part: TypoPart | undefined, path: string, push: Push): void {
  const size = part?.size;
  if (size !== undefined && size < W.h1DesktopMin) {
    push({ code: "h1_small", path: `${path}.size`, value: size });
    return;
  }
  const phone = part?.sizePhone ?? (size !== undefined ? phoneFontSize(size) : undefined);
  if (phone !== undefined && phone < W.h1PhoneMin) {
    push({ code: "h1_small", path: part?.sizePhone !== undefined ? `${path}.sizePhone` : `${path}.size`, value: phone });
  }
}

/** Объявления в полосе сменяются: вписано больше одного или акций может быть больше одной. */
function rotates(el: SectionElementOf<"announcements">): boolean {
  return el.layout === "strip" && (el.source === "promotions" ? el.limit > 1 : el.items.length > 1);
}

/** Проверки сеток и смены (этап 3б), не зависящие от темы. */
function gridChecks(el: SectionElement, path: string, push: Push): void {
  if ((el.kind === "tiles" || el.kind === "categories") && el.cols >= W.gridColsManyFrom) {
    push({ code: "grid_cols_many", path: `${path}.cols`, value: el.cols });
  }
  // Светлый набор без затемнения читается только на тёмных фото; без фото плитка — на своём фоне.
  if (el.kind === "tiles" && el.look === "overlay" && el.tone === "light" && tileShadeOf(el) === 0) {
    if (el.items.some((tile) => tile.mediaId !== null)) push({ code: "tile_light_no_shade", path: `${path}.shade` });
  }
  if (el.kind === "announcements" && rotates(el) && el.intervalSec < W.announcementIntervalMin) {
    push({ code: "announcement_fast", path: `${path}.intervalSec`, value: el.intervalSec });
  }
}

/** Проверки одного элемента, не зависящие от темы. */
function elementChecks(el: SectionElement, path: string, push: Push): void {
  gridChecks(el, path, push);
  if (el.kind === "button" && el.height !== undefined && el.height < W.buttonHeightMin) {
    push({ code: "button_height_lt48", path: `${path}.height`, value: el.height });
  }
  if (el.kind === "heading" && el.level === 1) h1Small(el.typo?.main, `${path}.typo.main`, push);
  if (el.kind === "product-title") h1Small(el.typo?.title, `${path}.typo.title`, push);
  if (el.kind === "image") {
    el.overlays.forEach((overlay, i) => {
      if (overlay.plate?.density === 0) push({ code: "overlay_no_plate", path: `${path}.overlays.${i}.plate.density` });
    });
  }
}

/**
 * Таблица (этап 3б): контраст текста к фону шапки и полос. Свой цвет текста —
 * `typo.main.color`; «Авто» — текст секции по тону фона под таблицей
 * («Текст» или «На тёмном», как `surfaceToneOf`), на фото-фоне секции — по
 * набору текста фото (`look.bg.textTone`: light → «На тёмном», dark →
 * «Текст»). Под таблицей фото — заливка считается только непрозрачная.
 */
function tableFillContrast(
  el: SectionElementOf<"table">,
  path: string,
  bg: RgbaColor | null,
  colors: SiteColors,
  push: Push,
  photoTone: "light" | "dark" | undefined,
): void {
  const fills: ReadonlyArray<[key: "head" | "stripe", ref: SiteColorRef | undefined]> = [
    ["head", el.head],
    ["stripe", el.stripe],
  ];
  for (const [key, ref] of fills) {
    if (!ref) continue;
    const fill = requireHexColor(colorRefHex(ref, colors));
    if (!bg && fill.a < 1) continue;
    const base = bg ?? resolveSurface(colors, "pageBg");
    const under = compositeColors(fill, base);
    const own = el.typo?.main?.color;
    // Светлый набор фото (или тёмная подложка) — текст «На тёмном».
    const dark = bg ? surfaceToneOf(toHexColor(base), colors) === "dark" : photoTone === "light";
    const auto: SiteTextColor = { kind: "role", role: dark ? "onDark" : "text" };
    const ratio = contrastRatio(textOn(own ?? auto, colors, under), under);
    const code = contrastCode(ratio);
    if (code) push({ code, path: `${path}.${key}`, value: floorTenth(ratio) });
  }
}

/** Контраст элемента к фону колонки (или к заливке у кнопки с заливкой). */
function elementContrast(
  el: SectionElement,
  path: string,
  bg: RgbaColor | null,
  colors: SiteColors,
  push: Push,
  photoTone?: "light" | "dark",
): void {
  if (el.kind === "image") {
    el.overlays.forEach((overlay, i) => overlayContrast(overlay, `${path}.overlays.${i}`, colors, push));
    return;
  }
  // Шапка и полосы таблицы проверяются и без своего цвета текста (контраст «Авто» к заливке).
  if (el.kind === "table") tableFillContrast(el, path, bg, colors, push, photoTone);
  if (!("typo" in el) || onOwnPhoto(el)) return;
  if (onSiteCard(el)) {
    typoContrast(el.typo, path, resolveSurface(colors, "cardBg"), colors, push);
    return;
  }
  if (el.kind === "button" && el.variant === "primary") {
    // Текст кнопки с заливкой лежит на заливке (своя или «Кнопки» темы), заливка — на фоне страницы.
    const page = resolveSurface(colors, "pageBg");
    const fill = compositeColors(requireHexColor(el.fill ? colorRefHex(el.fill, colors) : colors.button), page);
    typoContrast(el.typo, path, fill, colors, push);
    return;
  }
  if (bg) typoContrast(el.typo, path, bg, colors, push);
}

/**
 * Предупреждения об элементах секции. Реестр нужен всегда (удалённый свой
 * шрифт); тема — для контраста и числа семейств. Порядок — по месту в
 * секции, шрифты страницы — последними.
 */
export function sectionElementWarnings(
  payload: SectionPayload,
  registry: SiteFontRegistry,
  options: SectionElementWarningOptions = {},
): SectionElementWarning[] {
  const out: SectionElementWarning[] = [];
  const push: Push = (warning) => out.push(warning);
  const colors = options.theme?.colors;
  // Набор текста фото-фона секции — для «Авто» таблицы на фото (этап 3б).
  const photoTone = payload.look.bg.kind === "media" ? payload.look.bg.textTone : undefined;

  const header = payload.header;
  if (header) {
    if (header.level === 1) h1Small(header.typo?.title, "header.typo.title", push);
    const bg = colors ? backgroundOf(payload, -1, colors) : null;
    if (colors && bg) typoContrast(header.typo, "header", bg, colors, push);
  }

  payload.columns.items.forEach((column, col) => {
    const bg = colors ? backgroundOf(payload, col, colors) : null;
    column.elements.forEach((el, row) => {
      const path = `columns.items.${col}.elements.${row}`;
      elementChecks(el, path, push);
      if (colors) elementContrast(el, path, bg, colors, push, photoTone);
    });
  });

  const fonts = sectionTypoFonts(payload);
  for (const font of fonts) {
    if (!registry.has(font.key)) out.push({ code: "element_font_missing", path: font.path });
  }
  if (options.theme) {
    const base = [...themeFontKeys(options.theme), ...(options.pageFontKeys ?? [])];
    const count = countFontFamilies(base, [payload], registry);
    const own = fonts.find((f) => registry.has(f.key) && !base.includes(f.key));
    if (count > W.fontFamiliesMax && own) out.push({ code: "fonts_over_3", path: own.path, value: count });
  }
  return out;
}
